import { SurveyRecipient } from "../../types/survey";

export const parseCSVLine = (
  line: string,
  delimiter: string = ","
): string[] => {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;
  let escapeNext = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (escapeNext) {
      current += char;
      escapeNext = false;
    } else if (char === '"' && inQuotes && line[i + 1] === '"') {
      // Handle escaped quotes ("" within quoted field)
      current += char;
      escapeNext = true;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

export const readCSVFile = (
  file: File,
  t?: (key: string) => string
): Promise<SurveyRecipient[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // Clean up function to prevent memory leaks
    const cleanup = () => {
      reader.onload = null;
      reader.onerror = null;
      reader.onabort = null;
    };

    reader.onload = () => {
      try {
        const fileContent = reader.result as string;
        const lines = fileContent
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        if (lines.length === 0) {
          cleanup();
          reject(new Error("File is empty"));
          return;
        }

        // Detect delimiter (check if semicolons are more common than commas)
        const firstLine = lines[0];
        const semicolonCount = (firstLine.match(/;/g) || []).length;
        const commaCount = (firstLine.match(/,/g) || []).length;
        const delimiter = semicolonCount > commaCount ? ";" : ",";

        // Parse CSV headers
        const headers = parseCSVLine(lines[0], delimiter).map((header) =>
          header.trim().toLowerCase().replace(/"/g, "")
        );

        const emailIndex = headers.findIndex((h) => h === "email");
        const phoneIndex = headers.findIndex(
          (h) =>
            h === "phonenumber" ||
            h === "phone_number" ||
            h === "phone" ||
            h === "phone number"
        );

        if (emailIndex === -1) {
          cleanup();
          reject(
            new Error(
              t?.("shareLink.upload.errors.missingEmailColumn") ||
                "CSV file must have an 'email' column"
            )
          );
          return;
        }

        const recipients: SurveyRecipient[] = [];

        // Parse data rows (skip header row)
        for (let i = 1; i < lines.length; i++) {
          const values = parseCSVLine(lines[i], delimiter).map(
            (value, index) => {
              const trimmed = value.trim().replace(/^"|"$/g, ""); // Remove surrounding quotes
              // Basic CSV injection protection - remove dangerous characters, but preserve + in phone numbers
              if (index === phoneIndex && trimmed.startsWith("+")) {
                // For phone numbers, only remove = - @ at the beginning
                return trimmed.replace(/^[=\-@]/, "");
              }
              // For other fields, remove all dangerous characters
              return trimmed.replace(/^[=+\-@]/, "");
            }
          );

          const email = values[emailIndex]?.trim();

          if (email && email.length > 0) {
            const phoneNumber =
              phoneIndex !== -1 ? values[phoneIndex]?.trim() : undefined;

            recipients.push({
              email,
              phone_number:
                phoneNumber && phoneNumber.length > 0 ? phoneNumber : undefined,
            });
          }
        }

        cleanup();
        resolve(recipients);
      } catch (error) {
        cleanup();
        reject(new Error("Error parsing CSV file"));
      }
    };

    reader.onerror = () => {
      cleanup();
      reject(new Error("Failed to read the file"));
    };

    reader.onabort = () => {
      cleanup();
      reject(new Error("File reading was aborted"));
    };

    reader.readAsText(file, "UTF-8");
  });
};
